/**
 * category_context -> dataLayer
 *
 * Resolves the current pathname against the navigation tree and pushes the
 * parent trail as level_1..level_4. All four keys are always present.
 *
 * Rules
 *   B) handle starts with "all-"  -> deepest trail
 *   C) handle ends with "-gifts"  -> trail rooted in the Gifts top-level item
 *   D) otherwise                  -> first match in document order
 *   E) no match / any error       -> all levels ''
 *
 * Spec and rules supplied by Head of Dev (2026-09-14). Loaded only on
 * collection pages (layout/theme.liquid, `template.name == 'collection'`),
 * deferred, independent of the WhittardAnalytics core (assets/analytics.js) —
 * this pushes to window.dataLayer directly rather than through A.push(), since
 * it's a page-load resolver rather than a DOM-event hook.
 *
 * The navigation tree it reads (CONFIG.getMenu) is window.__headerMenuTree,
 * built by snippets/menu-tree-json.liquid and rendered once from
 * sections/header.liquid, so it's present on every page.
 */
(function () {
  'use strict';

  var CONFIG = {
    eventName: 'category_context',
    levels: 4,
    // Where the navigation JSON lives — snippets/menu-tree-json.liquid,
    // rendered from sections/header.liquid on every page.
    getMenu: function () { return window.__headerMenuTree; },
    // Set to false to stay silent on pages with no match rather than
    // pushing an all-blank event.
    pushOnNoMatch: true
  };

  function emptyProperties() {
    var props = {};
    for (var i = 1; i <= CONFIG.levels; i++) props['level_' + i] = '';
    return props;
  }

  /**
   * Splits a menu URL or a live pathname into { type, handle }.
   * Strips the origin, query, hash, a Shopify Markets locale prefix and any
   * trailing slash, then takes the first segment as the resource type and the
   * last as the handle. Returns null for "/" placeholders and unusable input.
   */
  function parseUrl(url) {
    if (typeof url !== 'string' || !url) return null;
    var path = url.split('?')[0].split('#')[0];
    path = path.replace(/^https?:\/\/[^\/]+/i, '');
    path = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
    var segs = path.split('/').filter(Boolean);
    if (!segs.length) return null;
    return {
      type: segs[0].toLowerCase(),
      handle: segs[segs.length - 1].toLowerCase()
    };
  }

  /** Depth-first, document order. A parent is recorded before its children. */
  function collectMatches(nodes, trail, target, out) {
    if (!Array.isArray(nodes)) return out;
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!node || typeof node !== 'object') continue;
      var next = trail.concat([typeof node.title === 'string' ? node.title : '']);
      var parsed = parseUrl(node.url);
      if (parsed && parsed.type === target.type && parsed.handle === target.handle) {
        out.push(next);
      }
      if (Array.isArray(node.links)) collectMatches(node.links, next, target, out);
    }
    return out;
  }

  /** Title of the top-level item owning /collections/all-gifts, for rule C. */
  function giftsRootTitle(menu) {
    for (var i = 0; i < menu.length; i++) {
      var parsed = parseUrl(menu[i] && menu[i].url);
      if (parsed && parsed.handle === 'all-gifts') return menu[i].title;
    }
    return null;
  }

  function select(menu, matches, target) {
    if (!matches.length) return null;
    if (matches.length === 1) return matches[0];

    if (target.handle.indexOf('all-') === 0) {                      // B
      return matches.reduce(function (best, trail) {
        return trail.length > best.length ? trail : best;
      }, matches[0]);
    }

    if (/-gifts$/.test(target.handle)) {                            // C
      var root = giftsRootTitle(menu);
      if (root) {
        for (var i = 0; i < matches.length; i++) {
          if (matches[i][0] === root) return matches[i];
        }
      }
    }

    return matches[0];                                              // D
  }

  function resolve(menu, pathname) {
    var target = parseUrl(pathname);
    if (!target) return [];
    return select(menu, collectMatches(menu, [], target, []), target) || [];
  }

  function toProperties(trail) {
    var props = emptyProperties();
    if (!Array.isArray(trail)) return props;
    for (var i = 0; i < Math.min(trail.length, CONFIG.levels); i++) {
      props['level_' + (i + 1)] = typeof trail[i] === 'string' ? trail[i] : '';
    }
    return props;
  }

  function push(props) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: CONFIG.eventName,
      category_properties: props
    });
  }

  function run() {
    var props = emptyProperties();
    try {
      var menu = CONFIG.getMenu();
      if (Array.isArray(menu) && menu.length) {
        var trail = resolve(menu, window.location.pathname);
        if (!trail.length && !CONFIG.pushOnNoMatch) return;
        props = toProperties(trail);
      }
    } catch (e) {
      props = emptyProperties();                                    // E
    }
    push(props);
  }

  run();

  // Optional: expose for SPA navigations and for QA in the console.
  window.pushCategoryContext = run;
}());
